import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Camera,
  Sun,
  Tag,
  MessageSquare,
  Rocket,
  ShieldCheck,
  Ruler,
  Sparkles,
  Film,
  Images,
} from "lucide-react";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizedPath } from "@/lib/i18n/slug-map";

const TITLE = "Tips för att sälja prylar snabbare – lyckas med din annons | Sellavo";
const DESCRIPTION =
  "Konkreta tips för bättre annonser: fota prylen rätt, skriv en tydlig beskrivning, sätt rimligt pris, svara snabbt och lyft annonsen vid rätt tillfälle.";

export const Route = createFileRoute("/tips")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://sellavo.com/tips" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/tips" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Så säljer du snabbare på Sellavo",
          description: DESCRIPTION,
          step: [
            { "@type": "HowToStep", name: "Fota prylen i dagsljus mot en ren bakgrund" },
            { "@type": "HowToStep", name: "Komplettera texten med märke, modell, skick och mått" },
            { "@type": "HowToStep", name: "Sätt ett realistiskt pris utifrån liknande annonser" },
            { "@type": "HowToStep", name: "Svara snabbt i chatten" },
            { "@type": "HowToStep", name: "Lyft eller justera priset om annonsen står still" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "/" },
            { "@type": "ListItem", position: 2, name: "Tips", item: "/tips" },
          ],
        }),
      },
    ],
  }),
  component: TipsPage,
});

const fotoTips = [
  {
    icon: Camera,
    title: "En pryl per annons",
    text: "Blanda aldrig cykeln och TV:n i samma annons. Köpare söker på en sak – och vi skriver en mycket bättre text när bilderna visar samma pryl.",
  },
  {
    icon: Sun,
    title: "Dagsljus och ren bakgrund",
    text: "Ställ prylen framför en neutral vägg eller ett rent golv nära ett fönster. Undvik blixt, röriga bakgrunder och mörka hörn.",
  },
  {
    icon: Ruler,
    title: "Motivet i mitten",
    text: "Låt prylen fylla bilden och synas helt. Ta gärna en översiktsbild, en detaljbild och en bild på eventuellt slitage.",
  },
  {
    icon: ShieldCheck,
    title: "Var ärlig med skicket",
    text: "Repor, fläckar och saknade delar syns ändå vid visning. Ärliga bilder ger färre avhopp och snabbare affär.",
  },
];

export function TipsPage() {
  const { lang } = useLocale();
  if (lang !== "sv") return <TranslatedTips lang={lang} />;
  return (
    <main className="bg-background">
      <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-ocean-300">
            Sälj snabbt och snyggt
          </p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Tips för att sälja dina prylar snabbare
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ocean-100 sm:mt-5 sm:text-lg">
            Två annonser för samma pryl kan få helt olika resultat. Här är det som faktiskt gör
            skillnad – bilderna, texten, priset och hur snabbt du svarar.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
        <h2 className="font-heading text-2xl font-bold">1. Bilderna säljer</h2>
        <p className="mt-3 text-muted-foreground">
          Bilden är det första – och ofta det enda – en köpare tittar på i listan. Lägg någon minut
          extra här, så gör resten sig själv.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {fotoTips.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-md border border-border bg-card p-5">
              <Icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading text-base font-semibold">{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-heading text-2xl font-bold">
          2. Många prylar? Gör allt på en gång
        </h2>
        <p className="mt-3 text-muted-foreground">
          Ska du rensa ut ett helt förråd är det både långsamt och tråkigt att skapa en annons i
          taget. Två genvägar gör jobbet åt dig – båda listar prylarna med namn och ett uppskattat
          värde, som du sedan gör annonser av.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-5">
            <Film className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">
              Filma rummet – snabbast av allt
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Gå lugnt runt i rummet i upp till 60 sekunder. Det tar en halv minut att filma 30
              prylar, mot flera minuter att fota dem en och en. Vi plockar ut varje sak ur filmen
              och sparar en stillbild på den.
            </p>
            <Link
              to="/videoskanning"
              className="mt-3 inline-block text-sm font-medium text-primary underline underline-offset-4"
            >
              Så fungerar videoskanning
            </Link>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <Images className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">Fota i bulk</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Gå runt och fota fritt och ladda upp alla bilder på en gång. Vi grupperar ihop
              bilderna som visar samma pryl, så du får en färdig lista att gå igenom. Bäst bildkvalitet
              – och bilderna kan användas direkt i annonsen.
            </p>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Tips: filma först för att få överblick och värden, fota sedan de saker du faktiskt vill
          sälja. Värdena är uppskattningar – du bestämmer alltid pris och text själv.
        </p>

        <h2 className="mt-14 font-heading text-2xl font-bold">3. Låt oss skriva – och fyll på</h2>
        <p className="mt-3 text-muted-foreground">
          Du får rubrik och beskrivning färdigt, men du känner prylen bäst. Komplettera med sådant
          som inte syns på bilden: märke och modell, ålder, mått, om originalkartong och kvitto
          finns, varför du säljer och om något behöver lagas. Håll rubriken kort och konkret – den
          ska säga vad det är, inte låta som en reklamtext.
        </p>

        <h2 className="mt-14 font-heading text-2xl font-bold">4. Sätt ett pris som känns rätt</h2>
        <p className="mt-3 text-muted-foreground">
          Vårt prisförslag utgår från vad liknande prylar brukar säljas för. Kolla gärna några
          liknande annonser innan du bestämmer dig. Ett pris som ligger klart under marknadsvärdet
          känns ofta som en bluff och skrämmer bort seriösa köpare – ett för högt pris gör att
          annonsen aldrig får några klick. Är du osäker: lägg dig strax över det du minst vill ha,
          så finns utrymme att pruta.
        </p>

        <h2 className="mt-14 font-heading text-2xl font-bold">5. Svara snabbt</h2>
        <p className="mt-3 text-muted-foreground">
          Den som svarar först säljer oftast. All kontakt sker i inkorgen på Sellavo, så din
          e-postadress och ditt telefonnummer visas aldrig publikt. Vi skickar ett samlat mejl när
          du fått nya meddelanden, så att du inte missar en köpare.
        </p>

        <h2 className="mt-14 font-heading text-2xl font-bold">6. Står annonsen still?</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-5">
            <Rocket className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">Lyft annonsen</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Ett lyft placerar annonsen högst upp igen. Bäst effekt får du på kvällar och söndagar,
              när flest personer letar.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <Tag className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">Justera priset</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Har du haft visningar men inga meddelanden på en vecka är priset oftast orsaken. Sänk
              lite – hellre sålt än liggande i förrådet.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">Byt förstabild</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              En ny, ljusare förstabild kan ensam fördubbla antalet klick. Testa en annan vinkel.
            </p>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <MessageSquare className="h-5 w-5 text-primary" />
            <h3 className="mt-3 font-heading text-base font-semibold">Förnya i tid</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Annonser pausas automatiskt efter 60 dagar. Förnya den innan dess så tappar du inte
              statistiken.
            </p>
          </div>
        </div>

        <h2 className="mt-14 font-heading text-2xl font-bold">7. Trygghet och regler</h2>
        <p className="mt-3 text-muted-foreground">
          Skriv aldrig telefonnummer, e-postadresser eller länkar i annonsen – varken i texten eller
          i en video. Vår AI granskar allt som publiceras och stoppar sådant, dels för att skydda
          dig mot bedrägeriförsök, dels för att all kontakt ska kunna spåras om något går fel. Läs
          mer i våra{" "}
          <Link to="/regler" className="text-primary underline underline-offset-4">
            annonsregler
          </Link>
          .
        </p>

        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/skapa"
            className="rounded-md bg-primary px-5 py-3 font-heading font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Skapa annons
          </Link>
          <Link
            to="/annonser"
            className="rounded-md border border-input px-5 py-3 font-medium hover:bg-accent"
          >
            Se alla annonser
          </Link>
        </div>
      </section>
    </main>
  );
}

function TranslatedTips({ lang }: { lang: "en" | "es" }) {
  const en = lang === "en";
  const photo = en ? [
    ["One item per listing", "Do not mix unrelated items in one listing. Buyers search for one thing, and we write better text when every photo shows it."],
    ["Daylight and a clean background", "Place the item near a window against a neutral wall or clean floor. Avoid flash, clutter and dark corners."],
    ["Keep the subject centred", "Show the entire item. Include an overview, a detail and any visible wear."],
    ["Be honest about condition", "Honest photos mean fewer cancelled purchases and a faster deal."],
  ] : [
    ["Un objeto por anuncio", "No mezcles objetos distintos en un anuncio. Los compradores buscan una cosa y escribimos mejor cuando todas las fotos muestran el mismo objeto."],
    ["Luz natural y fondo limpio", "Coloca el objeto cerca de una ventana, ante una pared neutra o un suelo limpio. Evita el flash, el desorden y las zonas oscuras."],
    ["El objeto en el centro", "Muestra el objeto completo. Incluye una vista general, un detalle y cualquier desgaste visible."],
    ["Sé sincero sobre el estado", "Las fotos honestas reducen los abandonos y agilizan la operación."],
  ];
  const later = en ? [
    ["3. Let us write — then add your knowledge", "You receive a finished title and description, but you know the item best. Add its brand, model, age, measurements, accessories, receipt and any defects."],
    ["4. Choose a fair price", "Our suggestion is based on similar second-hand items. Compare a few listings and leave a little room for negotiation if you are unsure."],
    ["5. Reply quickly", "The first seller to reply often gets the sale. All contact stays in Sellavo, so your email address and phone number remain private."],
    ["7. Safety and rules", "Never put phone numbers, email addresses or links in the listing. We review published content to protect users and keep contact traceable."],
  ] : [
    ["3. Déjanos escribir y añade lo que sabes", "Recibes el título y la descripción terminados, pero tú conoces mejor el objeto. Añade marca, modelo, antigüedad, medidas, accesorios, recibo y posibles defectos."],
    ["4. Elige un precio justo", "Nuestra sugerencia se basa en artículos similares. Compara varios anuncios y deja un pequeño margen para negociar si tienes dudas."],
    ["5. Responde rápido", "Quien responde primero suele vender. Todo el contacto permanece en Sellavo, así que tu correo y teléfono siguen privados."],
    ["7. Seguridad y reglas", "Nunca incluyas teléfonos, correos ni enlaces en el anuncio. Revisamos el contenido para proteger a los usuarios y mantener el contacto trazable."],
  ];
  const improve = en ? [["Boost the listing", "A boost returns the listing to the top. Evenings and Sundays often work best."], ["Adjust the price", "Views without messages often indicate that the price is too high."], ["Change the first photo", "A brighter main photo can substantially increase clicks."], ["Renew in time", "Listings pause automatically after 60 days. Renew before then to retain statistics."]] : [["Destaca el anuncio", "Una promoción devuelve el anuncio a la parte superior. Las tardes y los domingos suelen funcionar mejor."], ["Ajusta el precio", "Muchas visitas sin mensajes suelen indicar que el precio es demasiado alto."], ["Cambia la primera foto", "Una imagen principal más luminosa puede aumentar considerablemente los clics."], ["Renueva a tiempo", "Los anuncios se pausan automáticamente después de 60 días. Renueva antes para conservar las estadísticas."]];
  const icons = [Camera, Sun, Ruler, ShieldCheck];
  const improveIcons = [Rocket, Tag, Sparkles, MessageSquare];
  return <main className="bg-background">
    <section className="bg-ocean-900 px-4 py-10 text-white sm:px-6 sm:py-16 lg:px-8"><div className="mx-auto max-w-4xl">
      <p className="text-xs font-semibold uppercase tracking-widest text-ocean-300">{en ? "Sell faster and smarter" : "Vende más rápido y mejor"}</p>
      <h1 className="mt-3 font-heading text-3xl font-extrabold sm:text-5xl">{en ? "Tips for selling your things faster" : "Consejos para vender tus cosas más rápido"}</h1>
      <p className="mt-4 text-ocean-100 sm:text-lg">{en ? "Photos, text, price and quick replies make the difference. Here is what works." : "Las fotos, el texto, el precio y las respuestas rápidas marcan la diferencia. Esto es lo que funciona."}</p>
    </div></section>
    <section className="mx-auto max-w-4xl px-4 pb-16 pt-8 sm:px-6">
      <h2 className="font-heading text-2xl font-bold">{en ? "1. Photos sell" : "1. Las fotos venden"}</h2>
      <p className="mt-3 text-muted-foreground">{en ? "The image is the first thing buyers see. A few extra minutes here make everything else easier." : "La imagen es lo primero que ven los compradores. Dedicarle unos minutos facilita todo lo demás."}</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">{photo.map(([title,text], i) => { const Icon=icons[i]; return <div key={title} className="rounded-md border border-border bg-card p-5"><Icon className="h-5 w-5 text-primary"/><h3 className="mt-3 font-semibold">{title}</h3><p className="mt-1 text-sm text-muted-foreground">{text}</p></div>})}</div>
      <h2 className="mt-14 font-heading text-2xl font-bold">{en ? "2. Many items? Do them together" : "2. ¿Muchos objetos? Hazlos todos juntos"}</h2>
      <p className="mt-3 text-muted-foreground">{en ? "Film a room for the fastest overview, or upload up to 30 photos at once. We identify and group the items so you can create separate listings." : "Graba una habitación para obtener una vista rápida o sube hasta 30 fotos a la vez. Identificamos y agrupamos los objetos para crear anuncios separados."}</p>
      {later.slice(0,3).map(([title,text]) => <section key={title}><h2 className="mt-14 font-heading text-2xl font-bold">{title}</h2><p className="mt-3 text-muted-foreground">{text}</p></section>)}
      <h2 className="mt-14 font-heading text-2xl font-bold">{en ? "6. Is the listing standing still?" : "6. ¿El anuncio no avanza?"}</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">{improve.map(([title,text], i) => { const Icon=improveIcons[i]; return <div key={title} className="rounded-md border border-border bg-card p-5"><Icon className="h-5 w-5 text-primary"/><h3 className="mt-3 font-semibold">{title}</h3><p className="mt-1 text-sm text-muted-foreground">{text}</p></div>})}</div>
      <section><h2 className="mt-14 font-heading text-2xl font-bold">{later[3][0]}</h2><p className="mt-3 text-muted-foreground">{later[3][1]} <Link to={localizedPath("/regler", lang)} className="text-primary underline">{en ? "Read the listing rules" : "Lee las reglas de anuncios"}</Link>.</p></section>
      <div className="mt-12 flex gap-3"><Link to={localizedPath("/skapa",lang)} className="rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground">{en ? "Create listing" : "Crear anuncio"}</Link><Link to={localizedPath("/annonser",lang)} className="rounded-md border border-input px-5 py-3 font-medium">{en ? "Browse listings" : "Ver anuncios"}</Link></div>
    </section>
  </main>;
}
